// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';

// bernie
import { AnalyticsStore, ContextStore, RouteStore } from 'bernie-core';

// shared-ui
import { ILocalization, LocalizedComponentProps, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { Program, UserInfo } from 'models';

// stores
import { UserAuthStore } from 'stores';

// ui
import { UitkCard } from 'uitk-react-cards';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacingHr, UitkSpacing } from 'uitk-react-spacing';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkText } from 'uitk-react-text';
import { LocalizedTextWrapping } from 'components/localized-text-wrapping';

// components
import { ProgramCardFooter } from './program-card-footer';
import { ProgramCardFeatures } from './program-card-features';
import { ProgramCardHeader } from './program-card-header';
import { ProgramCardLobs } from './program-card-lobs';
import { ProgramCardLogo } from './program-card-logo';
import { ProgramCardStatusBadge } from './program-card-status-badge';
import { VerifyPartnerDialog } from '../verify-partner-dialog';
import { JoinAffiliateNetworkDialog } from '../join-affiliate-network-dialog';
import { CommercialRulesDialog } from '../commercial-rules-dialog';

export interface ProgramCardProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  l10n?: ILocalization;
  program: Program;
  partnerId: string;
  camref?: string;
  programStatus: string;
  routeStore: RouteStore;
  user: UserInfo;
  userAuth: UserAuthStore;
  layout: string;
}

export const ProgramCard: React.FunctionComponent<ProgramCardProps> = ({
  analytics,
  context,
  l10n,
  program,
  partnerId,
  camref,
  programStatus,
  routeStore,
  user,
  userAuth,
  layout,
}) => {
  const partnerSignedUp = partnerId && programStatus && programStatus !== 'UNKNOWN';
  const isProgramApproved = partnerId && programStatus === 'APPROVED';
  const [programStatusVerified, setProgramStatusVerified] = React.useState(false);

  function updateProgramStatusVerified() {
    setProgramStatusVerified(true);
  }

  const isCard = layout === 'card';

  const renderAddCID = () => {
    if (!partnerSignedUp && !programStatusVerified) {
      if (program.network === 'CJ') {
        return (
          <UitkSpacing padding={{ blockstart: 'six' }}>
            <UitkText align={isCard ? 'left' : 'right'} size={200}>
              <LocalizedTextWrapping
                l10n={l10n}
                message="programs.cards.content.have_an_account"
                fieldName="programs.cards.content.link.add_your_cid"
              >
                <UitkLink size="small">
                  <Link to={{ search: `pwaDialog=verifyPartner-${program.name}` }}>
                    <LocalizedText message="programs.cards.content.link.add_your_cid" />
                  </Link>
                </UitkLink>
              </LocalizedTextWrapping>
            </UitkText>
          </UitkSpacing>
        );
      } else {
        return <></>;
      }
    }
  };

  return (
    <UitkLayoutFlexItem grow={1} minWidth={isCard ? 384 : 'full_width'}>
      <UitkCard className={`ProgramCard ${layout ? `ProgramCard--${layout}` : ''}`}>
        <UitkLayoutFlex direction={isCard ? 'column' : 'row'} wrap="wrap">
          <UitkLayoutFlexItem
            className="ProgramCard__header"
            maxWidth={isCard ? 'full_width' : 160}
            grow={isCard ? 0 : 1}
          >
            <UitkLayoutFlex
              direction={isCard ? 'row' : 'column'}
              alignItems={'start'}
              justifyContent={isCard ? 'space-between' : 'start'}
            >
              <UitkLayoutFlexItem>
                <ProgramCardLogo program={program} />
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>
                <ProgramCardStatusBadge program={program} programStatus={programStatus} l10n={l10n} />
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem
            className="ProgramCard__body"
            minWidth={isCard ? 'zero' : 450}
            shrink={0}
            grow={isCard ? 1 : 0}
          >
            <UitkLayoutFlex direction={'column'} alignItems={'start'}>
              <UitkLayoutFlexItem>
                <ProgramCardHeader l10n={l10n} program={program} />
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>
                {program.lobs && <ProgramCardLobs l10n={l10n} program={program} />}
              </UitkLayoutFlexItem>
              <UitkSpacingHr blockMargin={isCard ? 'two' : 'four'} />
              <UitkLayoutFlexItem>
                {program.features && <ProgramCardFeatures l10n={l10n} program={program} layout={layout} />}
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>{renderAddCID()}</UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem className="ProgramCard__footer" minWidth={isCard ? 'zero' : 160} grow={isCard ? 0 : 1}>
            <UitkLayoutFlex
              space={isCard ? 'half' : 'four'}
              direction={isCard ? 'row' : 'column-reverse'}
              alignItems={isCard ? 'stretch' : 'end'}
              justifyContent={isCard ? 'space-between' : 'center'}
            >
              <ProgramCardFooter
                analytics={analytics}
                camref={camref}
                context={context}
                l10n={l10n}
                partnerId={partnerId}
                partnerSignedUp={partnerSignedUp}
                program={program}
                layout={layout}
                isProgramApproved={isProgramApproved}
                programStatus={programStatus}
                programStatusVerified={programStatusVerified}
              />
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>

        <VerifyPartnerDialog
          dialogId={`verifyPartner-${program.name}`}
          l10n={l10n}
          program={program}
          routeStore={routeStore}
          user={user}
          userAuth={userAuth}
          updateProgramStatusVerified={updateProgramStatusVerified}
        />
        <JoinAffiliateNetworkDialog
          dialogId={`joinAffiliateNetwork-${program.name}`}
          l10n={l10n}
          routeStore={routeStore}
          locale={context.locale}
          isPreviewMode={false}
          showManageProgramLink={false}
          program={program}
        />
        <CommercialRulesDialog
          dialogId={`commercialRules-${program.name}`}
          l10n={l10n}
          routeStore={routeStore}
          program={program}
        />
      </UitkCard>
    </UitkLayoutFlexItem>
  );
};
